import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Play, Filter, Youtube, Clock } from "lucide-react";
import PageTransition from "../components/PageTransition";
import YouTubeModal, { ytId } from "../components/YouTubeModal";
import { Laurel } from "../components/Laurel";

const CATEGORIES = ["All", "AI Short Films", "Hackathon Films", "Spec Ads"];

const PROJECTS = [
  {
    slug: "the-legend-of-birsa-munda",
    title: "The Legend of Birsa Munda",
    category: "AI Short Films",
    badge: "Featured · Award Winner",
    client: "MIFF 2026 · IFFI 2025 · Miami · Budapest",
    year: "2025",
    summary:
      "The story of Birsa Munda — the legendary tribal revolutionary of India — told through the lens of generative AI. Officially selected in the Non-Competition Section of the 19th Mumbai International Film Festival 2026, and showcased at the India AI Impact Summit, AI Film Festival at IFFI 2025, Miami Art Tech Summit and Budapest SmartFest.",
    laurel: "19th Mumbai International Film Festival 2026",
    poster: "/work/birsa-munda-poster.jpg",
    stills: [
      "/work/birsa-munda-still-1.jpg",
      "/work/birsa-munda-still-2.jpg",
      "/work/birsa-munda-still-3.jpg",
    ],
    youtube: "https://youtu.be/Bu2072Hixx4",
    aspect: "16/10",
    featured: true,
  },
  {
    slug: "lost-and-found",
    title: "Lost & Found",
    category: "Hackathon Films",
    badge: "Best Storyteller · CinemAI 2025",
    client: "CinemAI Hackathon · IFFI 2025",
    year: "2025",
    summary:
      "Best Storyteller Winner at the CinemAI Hackathon, IFFI 2025. Made in 48 hours — a testament to what's possible when story, pressure and AI tools collide.",
    poster: "/work/lost-and-found-poster.png",
    youtube: "https://youtu.be/BTAduL20x3I",
    aspect: "3/4",
  },
  {
    slug: "the-window",
    title: "The Window",
    category: "Hackathon Films",
    badge: "IFFD 2026 · 48-Hour Film",
    client: "AI Filmmaking Hackathon · IFFD 2026",
    year: "2026",
    summary:
      "A 48-Hour AI Short Film created at the Filmmaking Hackathon, IFFD 2026. A meditation on perspective, isolation and the human need for connection.",
    poster: "/work/the-window-poster.png",
    youtube: "https://youtu.be/bERbbcypOEg",
    aspect: "16/10",
  },
  {
    slug: "arunima",
    title: "Arunima — The One Who Rose Above",
    category: "AI Short Films",
    badge: "True Story",
    client: "Independent Tribute Film",
    year: "2026",
    summary:
      "The true story of Arunima Sinha — the first female amputee to conquer Mount Everest. A film about grit, courage and the unbreakable human spirit.",
    poster: "/work/arunima-poster.jpg",
    youtube: "https://youtu.be/qxLtYHYvV8Y",
    aspect: "4/5",
  },
  {
    slug: "baraat",
    title: "Baraat",
    category: "AI Short Films",
    badge: "Official Selection · NOMOREFROG AI Film Awards",
    client: "NOMOREFROG AI Film Awards · South Korea",
    year: "2026",
    summary:
      "Some baraats never reach their destination. A moonlit wedding procession, a road through the desert, and a night that refuses to end — a cinematic AI thriller steeped in Indian folklore. Officially selected at the NOMOREFROG AI Film Awards, South Korea.",
    poster: "/work/baraat-poster.jpg",
    youtube: "https://youtu.be/mIIXUCY3Ifg",
    laurel: "NOMOREFROG AI Film Awards · S.Korea",
    aspect: "3/2",
  },
  {
    slug: "viraam",
    title: "Viraam",
    category: "AI Short Films",
    badge: "Created for the Higgsfield AI Film Festival",
    client: "Higgsfield AI Film Festival Entry",
    year: "2026",
    summary:
      "A moment. A memory. A war within. Some battles never end — they just pause. A soldier, a child's drawing, and the silence between the shells. Created for the Higgsfield AI Film Festival.",
    poster: "/work/viraam-poster.jpg",
    youtube: "https://youtu.be/exKcpUoDXW4",
    aspect: "16/9",
  },
  {
    slug: "the-last-drop",
    title: "The Last Drop",
    category: "AI Short Films",
    badge: "Debut Film",
    client: "YouTube Release",
    year: "2026",
    summary:
      "The film that started it all. A deeply personal exploration of the global water crisis — told through cinematic AI visuals and raw emotional storytelling.",
    poster: "/work/the-last-drop-poster.jpg",
    youtube: "https://youtu.be/waUh20lTUw8",
    aspect: "3/4",
  },
  {
    slug: "the-tricolor",
    title: "The Tricolor",
    category: "AI Short Films",
    badge: "Independence Day Special",
    client: "India's Pride & Freedom in Motion",
    year: "2026",
    summary:
      "India's Pride & Freedom in Motion — an Independence Day special. A patriotic meditation on the spirit of the tricolor, told through cinematic AI.",
    poster: "/work/the-tricolor-poster.jpg",
    youtube: "https://youtu.be/i5gUyxRNDBI",
    aspect: "4/3",
  },
  {
    slug: "arc-spec-ad",
    title: "ARC — Outrun the Dark",
    category: "Spec Ads",
    badge: "Spec Advertisement",
    client: "ARC · Sports Brand Spec",
    year: "2026",
    summary:
      "A cinematic spec ad for ARC — built entirely with AI. Proof that brand storytelling through AI can match — and exceed — the power of traditional production.",
    poster: "/work/arc-spec-ad-poster.png",
    youtube: "https://youtu.be/U29bvEiAm50",
    aspect: "16/10",
  },
];

/* ------------------ Header ------------------ */
const Header = () => (
  <section className="relative bg-ink pt-40 pb-16 md:pt-52 md:pb-20" data-testid="work-header">
    <div className="mx-auto max-w-[1500px] px-6 md:px-12">
      <p className="overline mb-6">The Archive</p>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
        <div className="md:col-span-9 overflow-hidden">
          <motion.h1
            initial={{ y: "8%", opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1.1, ease: [0.22, 0.65, 0.2, 1] }}
            className="font-serif text-bone leading-[0.92] tracking-tight"
            style={{ fontSize: "clamp(2.4rem, 8vw, 8rem)" }}
            data-testid="work-headline"
          >
            Films that <span className="italic text-gold">feel</span>.
            <br />
            Stories that stay.
          </motion.h1>
        </div>
        <div className="md:col-span-3">
          <p className="text-bone/65 font-sans text-sm md:text-base leading-relaxed">
            Every project at Cine Prompt begins with a story worth telling — and ends with a film worth watching.
          </p>
        </div>
      </div>
      <div className="hairline mt-12 md:mt-16" />
    </div>
  </section>
);

/* ------------------ Featured Project ------------------ */
const FeaturedProject = ({ project, onPlay }) => (
  <section className="relative bg-ink pb-20 md:pb-28" data-testid="work-featured">
    <div className="mx-auto max-w-[1500px] px-6 md:px-12">
      <div className="flex items-center gap-3 mb-6">
        <span className="w-2 h-2 rounded-full bg-gold animate-pulse" />
        <span className="overline">Featured Project · Now playing</span>
      </div>

      <button
        type="button"
        onClick={() => onPlay(project)}
        data-testid="work-featured-card"
        className="group block w-full text-left relative cp-frame cp-glow-border aspect-[3/4] sm:aspect-[16/10] md:aspect-[21/9]"
      >
        <img
          src={project.poster}
          alt={project.title}
          loading="eager"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: "saturate(0.92) contrast(1.04) brightness(0.78)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/70 via-transparent to-ink/30" />

        {/* Centered play button */}
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="inline-flex items-center justify-center w-20 h-20 md:w-24 md:h-24 rounded-full border border-gold bg-ink/40 backdrop-blur-sm group-hover:bg-gold transition-colors duration-500">
            <Play size={28} className="text-gold group-hover:text-ink ml-1 transition-colors duration-500" fill="currentColor" />
          </span>
        </span>

        {/* Top meta */}
        <div className="absolute top-6 left-6 right-6 flex items-center justify-between text-bone/75 text-[10px] tracking-[0.3em] uppercase">
          <span className="text-gold">{project.badge || project.category}</span>
          <span className="hidden sm:inline">{project.year} · {project.client}</span>
        </div>
        {project.laurel && (
          <div className="absolute top-14 right-6 md:top-16 hidden md:block" data-testid="work-featured-laurel">
            <Laurel label={project.laurel} />
          </div>
        )}

        {/* Bottom content */}
        <div className="absolute inset-x-0 bottom-0 p-8 md:p-12 lg:p-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-8">
              <h2
                className="font-serif text-bone tracking-tight leading-[0.95]"
                style={{ fontSize: "clamp(2rem, 5vw, 4.5rem)" }}
              >
                {project.title}
              </h2>
              <p className="mt-5 text-bone/80 font-sans text-base md:text-lg leading-relaxed max-w-2xl hidden sm:block">
                {project.summary}
              </p>
            </div>
            <div className="md:col-span-4 md:text-right hidden md:block">
              <span className="cp-btn cp-btn--glow inline-flex">
                <Play size={12} /> Watch on YouTube
              </span>
            </div>
          </div>
        </div>
      </button>

      {/* Frame gallery for featured (Birsa Munda) */}
      {project.stills && project.stills.length > 0 && (
        <div className="mt-6 md:mt-8" data-testid="work-featured-stills">
          <div className="flex items-center justify-between mb-4">
            <p className="overline">Frame gallery · From the film</p>
            <p className="text-bone/45 font-sans text-[10px] tracking-[0.3em] uppercase">
              {project.stills.length} stills
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
            {project.stills.map((src, i) => (
              <button
                type="button"
                key={src}
                onClick={() => onPlay(project)}
                className="cp-frame relative overflow-hidden border border-gold/15 hover:border-gold/40 transition-colors"
                style={{ aspectRatio: "16/10" }}
                data-testid={`work-featured-still-${i + 1}`}
              >
                <img
                  src={src}
                  alt={`${project.title} — still ${i + 1}`}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/55 to-transparent" />
                <span className="absolute bottom-3 right-3 text-bone/70 text-[10px] tracking-[0.3em] uppercase">
                  Still {String(i + 1).padStart(2, "0")}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  </section>
);

/* ------------------ Filter Tabs ------------------ */
const FilterTabs = ({ active, onChange, counts }) => (
  <section className="sticky top-16 md:top-20 z-30 bg-ink/85 backdrop-blur-md border-y border-gold/20" data-testid="work-filters">
    <div className="mx-auto max-w-[1500px] px-6 md:px-12 py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
      <div className="flex items-center gap-3 text-bone/55 text-[10px] tracking-[0.3em] uppercase">
        <Filter size={13} className="text-gold" />
        <span>Filter</span>
      </div>
      <div className="flex flex-wrap items-center gap-2 md:gap-3">
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            onClick={() => onChange(cat)}
            data-testid={`work-filter-${cat.toLowerCase().replace(/ /g, "-")}`}
            className={`cp-filter ${active === cat ? "cp-filter--active" : ""}`}
          >
            {cat}
            <span className="cp-filter__count">{`(${counts[cat] || 0})`}</span>
          </button>
        ))}
      </div>
    </div>
  </section>
);

/* ------------------ Project Card ------------------ */
const ProjectCard = ({ p, index, onPlay }) => {
  const hasVideo = Boolean(p.youtube);
  return (
  <motion.article
    layout
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -10 }}
    transition={{ duration: 0.6, delay: Math.min(index * 0.05, 0.4), ease: [0.22, 0.65, 0.2, 1] }}
    className="group break-inside-avoid mb-6 md:mb-8"
    data-testid={`work-card-${p.slug}`}
  >
    <button
      type="button"
      onClick={() => hasVideo && onPlay(p)}
      disabled={!hasVideo}
      aria-label={hasVideo ? `Play ${p.title}` : `${p.title} — coming soon`}
      className={`block w-full text-left ${hasVideo ? "" : "cursor-default"}`}
    >
      <div
        className="cp-frame relative overflow-hidden border border-bone/5"
        style={{ aspectRatio: p.aspect }}
      >
        <img
          loading="lazy"
          decoding="async"
          src={p.poster}
          alt={p.title}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: "saturate(0.92) contrast(1.04) brightness(0.85)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/10 to-transparent opacity-90 group-hover:opacity-60 transition-opacity duration-700" />

        {/* Hover play button / coming soon */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
          <div className="px-6 py-3 border border-gold/70 bg-ink/40 backdrop-blur-sm inline-flex items-center gap-2.5">
            {hasVideo ? (
              <>
                <Play size={12} className="text-gold" fill="currentColor" />
                <span className="text-gold font-sans text-[11px] tracking-[0.3em] uppercase">
                  Watch Film
                </span>
              </>
            ) : (
              <>
                <Clock size={12} className="text-gold" />
                <span className="text-gold font-sans text-[11px] tracking-[0.3em] uppercase">
                  Coming Soon
                </span>
              </>
            )}
          </div>
        </div>

        {/* Top tag */}
        <div className="absolute top-4 left-4 right-4 flex items-start justify-between gap-3 text-[10px] tracking-[0.3em] uppercase">
          <span className="text-gold">{p.category}</span>
          {hasVideo ? (
            <span className="inline-flex items-center gap-1.5 text-bone/70">
              <Youtube size={12} className="text-gold" /> YT
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-bone/70" data-testid={`work-card-${p.slug}-coming-soon`}>
              <Clock size={12} className="text-gold" /> Soon
            </span>
          )}
        </div>
        {p.laurel && (
          <div className="absolute top-10 right-4 md:top-11" data-testid={`work-card-${p.slug}-laurel`}>
            <Laurel label={p.laurel} />
          </div>
        )}
        {/* Bottom title */}
        <div className="absolute inset-x-0 bottom-0 p-5 md:p-6">
          {p.badge && (
            <p className="text-gold/90 text-[10px] tracking-[0.3em] uppercase mb-2">
              {p.badge}
            </p>
          )}
          <h3
            className="font-serif text-bone leading-tight tracking-tight"
            style={{ fontSize: "clamp(1.3rem, 1.8vw, 2rem)" }}
          >
            {p.title}
          </h3>
          <div className="flex items-baseline justify-between gap-3 text-bone/55 text-[10px] tracking-[0.3em] uppercase mt-3">
            <span>{p.year}</span>
            <span className="truncate ml-3">{p.client}</span>
          </div>
        </div>
      </div>
    </button>
  </motion.article>
  );
};

/* ------------------ Masonry Grid ------------------ */
const MasonryGrid = ({ items, onPlay }) => (
  <section className="relative bg-ink py-16 md:py-24" data-testid="work-grid">
    <div className="mx-auto max-w-[1500px] px-6 md:px-12">
      {items.length === 0 ? (
        <div className="py-32 text-center">
          <p className="overline mb-4">No films yet</p>
          <p className="text-bone/65 font-sans">This category is still loading frames. Try another filter.</p>
        </div>
      ) : (
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 md:gap-8" data-testid="work-grid-columns">
          <AnimatePresence>
            {items.map((p, i) => (
              <ProjectCard key={p.slug} p={p} index={i} onPlay={onPlay} />
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  </section>
);

/* ------------------ Story CTA ------------------ */
const StoryCTA = () => (
  <section className="cp-noise-cta py-32 md:py-44 mt-12 border-y border-gold/30" data-testid="work-story-cta">
    <div className="relative z-10 mx-auto max-w-[1300px] px-6 md:px-12 text-center">
      <p className="overline mb-6">Now booking · Spring MMXXVI</p>
      <h2
        className="font-serif text-bone tracking-tight leading-[0.98] mx-auto max-w-4xl"
        style={{ fontSize: "clamp(2.6rem, 7.6vw, 7rem)" }}
        data-testid="work-cta-headline"
      >
        Let's create <span className="italic text-gold">your</span> story.
      </h2>
      <p className="mt-8 text-bone/75 font-sans text-base md:text-lg max-w-xl mx-auto leading-relaxed">
        Brand films, corporate films, spec ads or your next short — Cine Prompt builds
        cinematic AI films that feel hand-made and travel the world.
      </p>
      <div className="mt-12 flex flex-wrap items-center justify-center gap-5">
        <a href="/contact" className="cp-btn cp-btn--glow" data-testid="work-cta-begin">
          Begin a project <ArrowUpRight size={14} />
        </a>
        <a href="/about" className="cp-btn cp-btn--ghost" data-testid="work-cta-about">
          About the studio
        </a>
      </div>
    </div>
  </section>
);

/* ------------------ Page ------------------ */
const Work = () => {
  const [active, setActive] = useState("All");
  const [playing, setPlaying] = useState(null);

  const featured = useMemo(() => PROJECTS.find((p) => p.featured) || PROJECTS[0], []);
  const rest = useMemo(() => PROJECTS.filter((p) => p.slug !== featured.slug), [featured.slug]);

  const filtered = useMemo(() => {
    if (active === "All") return rest;
    return rest.filter((p) => p.category === active);
  }, [active, rest]);

  const counts = useMemo(() => {
    const c = { All: PROJECTS.length };
    CATEGORIES.slice(1).forEach((cat) => {
      c[cat] = PROJECTS.filter((p) => p.category === cat).length;
    });
    return c;
  }, []);

  return (
    <PageTransition testId="page-work">
      <Header />
      <FeaturedProject project={featured} onPlay={setPlaying} />
      <FilterTabs active={active} onChange={setActive} counts={counts} />
      <MasonryGrid items={filtered} onPlay={setPlaying} />
      <StoryCTA />
      <AnimatePresence>
        {playing && (
          <YouTubeModal project={playing} onClose={() => setPlaying(null)} />
        )}
      </AnimatePresence>
    </PageTransition>
  );
};

export default Work;
